import PropTypes from 'prop-types'
import { Fragment } from 'react'
import moment from 'moment'

import theme from 'theme'
import SignedOutForm, {
  SignedOutFormContainer
} from 'components/authentication/SignedOutForm'
import BusyOverlay from 'components/common/BusyOverlay'
import Button from 'components/common/Button'
import DividerWithText from 'components/common/DividerWithText'
import Text from 'components/common/Text'
import TextButton from 'components/common/TextButton'

export default function AccountNameConflictForm(props) {
  const {
    companyName,
    disabled,
    handleCompanyNameChange,
    handleSignUp,
    handleUnsetConflicted,
    handleInviteRequest,
    inviteRequested
  } = props

  const date = moment(new Date()).format('DDMMYY')
  const alternateNames = [`${companyName} ${date}`, `${companyName}_${date}`]

  return (
    <SignedOutFormContainer>
      <SignedOutForm
        title="Account Setup"
        onSubmit={event => {
          event.preventDefault()
          handleInviteRequest && handleInviteRequest()
        }}
      >
        {disabled ? (
          <BusyOverlay shown={true} />
        ) : (
          <ConflictPrompt
            alternateNames={alternateNames}
            companyName={companyName}
            handleCompanyNameChange={handleCompanyNameChange}
            handleSignUp={handleSignUp}
            handleUnsetConflicted={handleUnsetConflicted}
            inviteRequested={inviteRequested}
          />
        )}
      </SignedOutForm>
    </SignedOutFormContainer>
  )
}

function ConflictPrompt(props) {
  const {
    companyName,
    alternateNames,
    handleCompanyNameChange,
    handleSignUp,
    handleUnsetConflicted,
    inviteRequested
  } = props

  return (
    <Fragment>
      {inviteRequested ? (
        <Text as="p" color="dark">
          We&apos;ve notified <strong>{companyName}</strong> about your request
        </Text>
      ) : (
        <Fragment>
          <Text as="p" color="dark" css={{ marginBottom: theme.space[3] }}>
            We&apos;re unable to create the company{' '}
            <strong>{companyName}</strong>
          </Text>

          <Text as="p" color="dark" css={{ marginBottom: theme.space[3] }}>
            This company already exists, request an invite:
          </Text>

          <Button
            data-test-account-setup-continue
            stretch
            label="Request Invite"
            type="submit"
          />

          <DividerWithText />

          <Text as="p" color="dark" css={{ marginBottom: theme.space[3] }}>
            Choose a suggested company name below:
          </Text>

          <CompanyNameAlternatives
            alternateNames={alternateNames}
            handleCompanyNameChange={handleCompanyNameChange}
            handleSignUp={handleSignUp}
            handleUnsetConflicted={handleUnsetConflicted}
          />
        </Fragment>
      )}
    </Fragment>
  )
}

function CompanyNameAlternatives(props) {
  const {
    alternateNames,
    handleSignUp,
    handleUnsetConflicted,
    handleCompanyNameChange
  } = props

  return (
    <ul css={{ listStyle: 'none' }}>
      {alternateNames.map((name, idx) => (
        <li key={idx}>
          <TextButton
            creative
            css={{ margin: '0 auto' }}
            onClick={event => {
              event.preventDefault()
              handleCompanyNameChange(
                name,
                () => handleSignUp && handleSignUp()
              )
            }}
          >
            {name}
          </TextButton>
        </li>
      ))}
      <li key={alternateNames.length} css={{ marginTop: theme.space[3] }}>
        <TextButton
          creative
          css={{ margin: '0 auto' }}
          onClick={event => {
            event.preventDefault()
            handleUnsetConflicted()
          }}
        >
          (Pick a new name)
        </TextButton>
      </li>
    </ul>
  )
}

AccountNameConflictForm.propTypes = {
  companyName: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  handleCompanyNameChange: PropTypes.func,
  handleSignUp: PropTypes.func,
  handleUnsetConflicted: PropTypes.func,
  handleInviteRequest: PropTypes.func,
  inviteRequested: PropTypes.bool
}

AccountNameConflictForm.defaultProps = {
  companyName: '',
  disabled: false,
  inviteRequested: false
}

CompanyNameAlternatives.propTypes = {
  alternateNames: PropTypes.array,
  handleCompanyNameChange: PropTypes.func,
  handleSignUp: PropTypes.func,
  handleUnsetConflicted: PropTypes.func,
  inviteRequested: PropTypes.bool
}

ConflictPrompt.propTypes = {
  companyName: PropTypes.string,
  alternateNames: PropTypes.array,
  handleCompanyNameChange: PropTypes.func,
  handleSignUp: PropTypes.func,
  handleUnsetConflicted: PropTypes.func,
  inviteRequested: PropTypes.bool
}
